import {
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { ReactComponent as AttachmentIcon } from "../../Assets/paperclip-solid.svg";
import { ReactComponent as XIcon } from "../../Assets/xmark-sharp-solid.svg";
import { activateOnKey } from "../../Helper/a11y";
import { useConfigStore } from "../../Stores/Config";
import "./ComposerShell.scss";
import useAuroraPointerFollow from "./useAuroraPointerFollow";

// A queued attachment shared by both composers' upload flows (conversation +
// home hero). Both build the exact same queue shape, so the preview markup can
// live once, here in the shell.
export type ComposerAttachment = {
  id: string;
  file: File;
  status: "uploading" | "done";
  progress: number;
  url?: string;
};

type ComposerShellProps = {
  // Extra classes for the composer wrapper (the hero + takeoff modifiers, or
  // --ai-working). The base `chat-message-composer` class is always applied.
  className?: string;
  // Inline geometry for the takeoff glide (position:fixed top/left/width/transform).
  style?: CSSProperties;
  // When true the textarea/chat block is replaced by the audio-recording layout
  // (the recorder itself is passed in the toolbar), and the toolbar row switches
  // to its full-width recording variant.
  audioActive: boolean;
  // The composer's <textarea>. Each wrapper owns its own value/handlers/ref, so
  // it is injected rather than reimplemented here.
  textarea: ReactNode;
  // Optional strip docked at the top of the card (e.g. the "Replying to …"
  // banner) — shares the card's border/radius, so it must render inside.
  banner?: ReactNode;
  // Attachment upload error banner.
  errorMessage?: string;
  onDismissError?: () => void;
  // Queued attachment previews (uploading / ready), shown above the toolbar.
  attachments?: ComposerAttachment[];
  onRemoveAttachment?: (id: string) => void;
  // Hides the toolbar from assistive tech during the takeoff glide.
  toolbarAriaHidden?: boolean;
  // Toolbar content: attachment button, audio recorder, send button — these
  // differ per composer, so the wrappers supply them.
  children: ReactNode;
};

// Presentational shell shared by the conversation composer (ChatMessageComposer)
// and the home hero composer (WidgetChatComposer). It owns the composer's DOM
// shape + classes — the aurora beam, the textarea / error / attachment block,
// and the toolbar row — so the two composers can no longer drift structurally.
// ALL behaviour (send logic, takeoff choreography, attachment uploads, height
// reporting) lives in the wrappers, which drive the shell via props + the
// forwarded container ref.
// The one exception is the purely-decorative aurora cursor-follow, which is
// isolated in the useAuroraPointerFollow hook (it only writes CSS vars).
const ComposerShell = forwardRef<HTMLDivElement, ComposerShellProps>(
  function ComposerShell(
    {
      className = "",
      style,
      audioActive,
      banner,
      textarea,
      errorMessage,
      onDismissError,
      attachments,
      onRemoveAttachment,
      toolbarAriaHidden,
      children,
    },
    ref,
  ) {
    // The shell needs its own handle on the container (to attach the pointer
    // listener) AND must still forward the ref its wrappers rely on (takeoff
    // geometry, height reporting). Merge the two.
    const innerRef = useRef<HTMLDivElement | null>(null);
    const setContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    // Aurora only on v4 widgets AND when the project's "Composer glow"
    // setting is on (defaults OFF — `=== true`, so a project opts in via the
    // dashboard toggle). The `=== 4` stays explicit for the same reason: it is
    // a v4-era look, so a future v5 opts in deliberately rather than
    // inheriting it.
    const isV4Widget = useConfigStore((state) => state.getHomeVersion() === 4);
    const translateTextWithFallback = useConfigStore(
      (state) => state.translateTextWithFallback,
    );
    const composerGlowEnabled = useConfigStore(
      (state) => state.config?.homeComposerGlow === true,
    );
    const showAurora = isV4Widget && composerGlowEnabled;

    // While AI-working the crest is keyframe-swept and hover must not add its
    // own effects — the hook detaches and clears its inline vars. Without the
    // aurora it has nothing to drive at all.
    useAuroraPointerFollow(
      innerRef,
      !showAurora || className.includes("chat-message-composer--ai-working"),
    );

    return (
      <div
        ref={setContainerRef}
        className={`chat-message-composer ${className}`.trim()}
        style={style}
      >
        {showAurora && (
          <div className="chat-message-composer-aurora" aria-hidden="true">
            <div className="chat-message-composer-aurora-bloom"></div>
            <div className="chat-message-composer-aurora-shimmer"></div>
            <div className="chat-message-composer-aurora-shimmer-b"></div>
          </div>
        )}
        {banner}
        {!audioActive && (
          <div className="chat-message-composer-chat">
            {textarea}
            {errorMessage && (
              <div className="chat-message-composer-chat-error" role="alert">
                <span>{errorMessage}</span>
                <button
                  type="button"
                  aria-label="Dismiss attachment upload error"
                  onClick={onDismissError}
                >
                  <XIcon />
                </button>
              </div>
            )}
            {attachments && attachments.length > 0 && (
              <div className="chat-message-composer-chat-attachment">
                {attachments.map((item) => {
                  const fileType = item.file?.type ?? "";
                  const showProgress = item.status === "uploading";

                  if (fileType.includes("image")) {
                    return (
                      <div
                        key={item.id}
                        className="chat-message-composer-chat-attachment-image"
                      >
                        <div className="chat-message-composer-chat-attachment-image-inner">
                          <img
                            src={URL.createObjectURL(item.file)}
                            alt="Attachment"
                          />
                          {showProgress && (
                            <div
                              className="chat-message-composer-chat-attachment-progress"
                              style={{ width: `${item.progress}%` }}
                            />
                          )}
                        </div>
                        <div className="chat-message-composer-chat-attachment-image-remove">
                          <XIcon
                            role="button"
                            tabIndex={0}
                            aria-label={translateTextWithFallback(
                              "removeAttachment",
                              "Remove attachment",
                            )}
                            onClick={() => onRemoveAttachment?.(item.id)}
                            onKeyDown={activateOnKey(() =>
                              onRemoveAttachment?.(item.id),
                            )}
                          />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={item.id}
                      className="chat-message-composer-chat-attachment-file"
                    >
                      <AttachmentIcon className="chat-message-composer-chat-attachment-file-icon" />
                      <div className="chat-message-composer-chat-attachment-file-name">
                        {item.file.name}
                      </div>
                      <XIcon
                        className="chat-message-composer-chat-attachment-file-remove"
                        role="button"
                        tabIndex={0}
                        aria-label={translateTextWithFallback(
                          "removeAttachment",
                          "Remove attachment",
                        )}
                        onClick={() => onRemoveAttachment?.(item.id)}
                        onKeyDown={activateOnKey(() =>
                          onRemoveAttachment?.(item.id),
                        )}
                      />
                      {showProgress && (
                        <div
                          className="chat-message-composer-chat-attachment-progress"
                          style={{ width: `${item.progress}%` }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div
          className={
            audioActive
              ? "composer-buttons-audio-recording-active"
              : "composer-buttons"
          }
          aria-hidden={toolbarAriaHidden}
        >
          {children}
        </div>
      </div>
    );
  },
);

export default ComposerShell;
